import { ColumnDef } from '@tanstack/react-table';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { useOps } from '../../../../../hooks/useOps';
import {
  formatCurrencyBrlFromCents,
  formatDateIso,
  formatPhone,
  formatCnpj,
  formatCpf,
} from '../../../../../utils';
import { MuveFechamentoOperacaoRow, MuveFechamentoResponse, MuveFechamentoResumoRow } from '../types';
import { romaneioService } from '../../../../../api';

export interface WeekOption {
  segunda: string;
  domingo: string;
  label: string;
  isCurrentWeek: boolean;
}

function getMondayOfWeek(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const dow = d.getDay();
  const diff = dow === 0 ? 6 : dow - 1;
  d.setDate(d.getDate() - diff);
  return d;
}

function dateStrFromDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function addDaysToDate(d: Date, days: number): Date {
  const result = new Date(d);
  result.setDate(d.getDate() + days);
  return result;
}

function formatWeekLabel(segunda: string, domingo: string): string {
  const [sy, sm, sd] = segunda.split('-').map(Number);
  const [dy, dm, dd] = domingo.split('-').map(Number);
  const p = (n: number) => String(n).padStart(2, '0');
  if (sy === dy) {
    return `${p(sd)}/${p(sm)} – ${p(dd)}/${p(dm)}/${dy}`;
  }
  return `${p(sd)}/${p(sm)}/${sy} – ${p(dd)}/${p(dm)}/${dy}`;
}

function generateWeekOptions(numPastWeeks = 12): WeekOption[] {
  const today = new Date();
  const currentMonday = getMondayOfWeek(today);
  const currentSeconda = dateStrFromDate(currentMonday);
  const options: WeekOption[] = [];

  const currentDomingo = dateStrFromDate(addDaysToDate(currentMonday, 6));
  options.push({
    segunda: currentSeconda,
    domingo: currentDomingo,
    label: `Semana atual — ${formatWeekLabel(currentSeconda, currentDomingo)}`,
    isCurrentWeek: true,
  });

  for (let i = 1; i <= numPastWeeks; i++) {
    const monday = addDaysToDate(currentMonday, -7 * i);
    const segunda = dateStrFromDate(monday);
    const domingo = dateStrFromDate(addDaysToDate(monday, 6));
    options.push({ segunda, domingo, label: formatWeekLabel(segunda, domingo), isCurrentWeek: false });
  }

  return options;
}

const muveFechamentoQueryKeys = {
  all: ['muve-fechamento'] as const,
  byOpsAndWeek: (ops: string, segunda: string) =>
    [...muveFechamentoQueryKeys.all, ops, segunda] as const,
};

async function fetchMuveFechamento(
  ops: string,
  segunda: string,
  allowCurrentWeek: boolean
): Promise<MuveFechamentoResponse | null> {
  const response = await romaneioService.getMuveFechamento({
    ops,
    segunda,
    ...(allowCurrentWeek ? { allowCurrentWeek: true } : {}),
  });
  if (response.success) {
    return (response.data as unknown as MuveFechamentoResponse) ?? null;
  }
  throw new Error(response.message || 'Erro ao carregar dados de fechamento MUVE');
}

const downloadCsv = (rows: Record<string, unknown>[], filename: string) => {
  if (rows.length === 0) return;
  const headers = Object.keys(rows[0]);
  const csvContent = [
    headers.join(','),
    ...rows.map((row) =>
      headers
        .map((h) => {
          const v = String(row[h] ?? '');
          return v.includes(',') || v.includes('"') || v.includes('\n')
            ? `"${v.replace(/"/g, '""')}"`
            : v;
        })
        .join(',')
    ),
  ].join('\n');

  const blob = new Blob(['﻿' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const formatDoc = (doc: string): string => {
  const digits = doc.replace(/\D/g, '');
  if (digits.length === 11) return formatCpf(digits);
  if (digits.length === 14) return formatCnpj(digits);
  return doc;
};

export function useMuveFechamento() {
  const { currentOps } = useOps();

  const weekOptions = useMemo(() => generateWeekOptions(12), []);
  const [selectedSeconda, setSelectedSeconda] = useState<string>(weekOptions[1].segunda);

  const selectedWeekOption = weekOptions.find((w) => w.segunda === selectedSeconda) ?? weekOptions[1];
  const isCurrentWeek = selectedWeekOption.isCurrentWeek;

  const enabled = Boolean(currentOps);

  const query = useQuery({
    queryKey: muveFechamentoQueryKeys.byOpsAndWeek(currentOps || '', selectedSeconda),
    queryFn: () => fetchMuveFechamento(currentOps!, selectedSeconda, isCurrentWeek),
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
  });

  const data = query.data ?? null;

  const buildFilename = useCallback(
    (prefix: string, ext: string) => {
      if (!data) return `${prefix}.${ext}`;
      const { semanaCompleta, periodo } = data;
      const weekClosed = semanaCompleta && !isCurrentWeek;
      const suffix = weekClosed ? '' : '_SEMANA_NAO_FECHADA';
      return `${prefix}_${currentOps}_${periodo.segunda}_${periodo.domingo}${suffix}.${ext}`;
    },
    [data, currentOps, isCurrentWeek]
  );

  const handleDownloadResumo = useCallback(() => {
    if (!data) return;
    const rows = data.resumo.map((r) => ({
      cnpj_cpf: r.cnpjCpf,
      nome_completo: r.nomeCompleto ?? '',
      cpf: r.cpf ?? '',
      telefone: r.telefone,
      valor_face_agregado: r.valorFaceAgregado,
    }));
    downloadCsv(rows as Record<string, unknown>[], buildFilename('resumo_muve', 'csv'));
  }, [data, buildFilename]);

  const handleDownloadOperacoes = useCallback(() => {
    if (!data) return;
    const rows = data.operacoes.map((r) => ({
      cnpj_cpf: r.cnpjCpf,
      nome_completo: r.nomeCompleto ?? '',
      cpf: r.cpf ?? '',
      cnpj: r.cnpj ?? '',
      telefone: r.telefone,
      valor_face: r.valorFace,
      valor_compra: r.valorCompra,
      data_romaneio: r.dataRomaneio?.substring(0, 10) ?? '',
      data_vencimento: r.dataVencimento?.substring(0, 10) ?? '',
      data_solicitado: r.dataSolicitado?.substring(0, 19) ?? '',
      comprovante_bancario: r.comprovanteBancario ?? '',
    }));
    downloadCsv(rows as Record<string, unknown>[], buildFilename('operacoes_muve', 'csv'));
  }, [data, buildFilename]);

  const summaryStats = useMemo(() => {
    if (!data) return null;
    const totalEntregadores = data.resumo.length;
    const totalOperacoes = data.operacoes.length;
    const valorFaceTotal = data.resumo.reduce((acc, r) => acc + r.valorFaceAgregado, 0);
    const comComprovante = data.operacoes.filter((o) => o.comprovanteBancario).length;
    return { totalEntregadores, totalOperacoes, valorFaceTotal, comComprovante };
  }, [data]);

  const resumoColumns: ColumnDef<MuveFechamentoResumoRow>[] = useMemo(
    () => [
      {
        accessorKey: 'cnpjCpf',
        header: 'Documento',
        size: 160,
        cell: ({ getValue }) => (
          <span className="font-mono text-sm">{formatDoc(getValue() as string)}</span>
        ),
      },
      {
        accessorKey: 'nomeCompleto',
        header: 'Nome',
        size: 230,
        cell: ({ getValue }) => {
          const v = getValue() as string | null;
          return v ? (
            <span className="font-medium">{v}</span>
          ) : (
            <span className="text-[hsl(215,16%,70%)] italic text-sm">Não informado</span>
          );
        },
      },
      {
        accessorKey: 'cpf',
        header: 'CPF',
        size: 140,
        cell: ({ getValue }) => {
          const v = getValue() as string | null;
          return v ? (
            <span className="font-mono text-sm">{formatCpf(v.replace(/\D/g, ''))}</span>
          ) : (
            <span className="text-[hsl(215,16%,70%)]">—</span>
          );
        },
      },
      {
        accessorKey: 'telefone',
        header: 'Telefone',
        size: 150,
        cell: ({ getValue }) => (
          <span className="font-mono text-sm">{formatPhone(getValue() as string)}</span>
        ),
      },
      {
        accessorKey: 'valorFaceAgregado',
        header: 'Valor Face Total',
        size: 160,
        meta: { align: 'right' as const },
        cell: ({ getValue }) => (
          <span className="font-semibold text-emerald-700">
            {formatCurrencyBrlFromCents(getValue() as number)}
          </span>
        ),
      },
    ],
    []
  );

  const operacoesColumns: ColumnDef<MuveFechamentoOperacaoRow>[] = useMemo(
    () => [
      {
        accessorKey: 'cnpjCpf',
        header: 'Documento',
        size: 160,
        cell: ({ getValue }) => (
          <span className="font-mono text-sm">{formatDoc(getValue() as string)}</span>
        ),
      },
      {
        accessorKey: 'nomeCompleto',
        header: 'Nome',
        size: 200,
        cell: ({ getValue }) => {
          const v = getValue() as string | null;
          return v ? (
            <span className="font-medium">{v}</span>
          ) : (
            <span className="text-[hsl(215,16%,70%)] italic text-sm">Não informado</span>
          );
        },
      },
      {
        accessorKey: 'telefone',
        header: 'Telefone',
        size: 150,
        cell: ({ getValue }) => (
          <span className="font-mono text-sm">{formatPhone(getValue() as string)}</span>
        ),
      },
      {
        accessorKey: 'valorFace',
        header: 'Valor Face',
        size: 130,
        meta: { align: 'right' as const },
        cell: ({ getValue }) => (
          <span className="font-semibold text-emerald-700">
            {formatCurrencyBrlFromCents(getValue() as number)}
          </span>
        ),
      },
      {
        accessorKey: 'valorCompra',
        header: 'Valor Compra',
        size: 130,
        meta: { align: 'right' as const },
        cell: ({ getValue }) => (
          <span className="font-semibold text-blue-700">
            {formatCurrencyBrlFromCents(getValue() as number)}
          </span>
        ),
      },
      {
        accessorKey: 'dataRomaneio',
        header: 'Romaneio',
        size: 120,
        cell: ({ getValue }) => (
          <span className="text-sm tabular-nums">{formatDateIso(getValue() as string)}</span>
        ),
      },
      {
        accessorKey: 'dataVencimento',
        header: 'Vencimento',
        size: 120,
        cell: ({ getValue }) => (
          <span className="text-sm tabular-nums">{formatDateIso(getValue() as string)}</span>
        ),
      },
      {
        accessorKey: 'dataSolicitado',
        header: 'Solicitado em',
        size: 140,
        cell: ({ getValue }) => {
          const v = getValue() as string | null;
          return v ? (
            <span className="text-sm tabular-nums">{formatDateIso(v)}</span>
          ) : (
            <span className="text-[hsl(215,16%,70%)]">—</span>
          );
        },
      },
      {
        accessorKey: 'comprovanteBancario',
        header: 'Comprovante',
        size: 150,
        cell: ({ getValue }) => {
          const url = getValue() as string | null;
          if (!url) {
            return <span className="text-[hsl(215,16%,70%)] text-sm">Sem comprovante</span>;
          }
          return (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[hsl(210,35%,32%)] hover:text-[hsl(210,35%,22%)] underline inline-flex items-center gap-1 text-sm font-medium"
            >
              Ver comprovante
              <ExternalLink className="h-3 w-3" />
            </a>
          );
        },
      },
    ],
    []
  );

  return {
    currentOps,
    data,
    summaryStats,
    loading: query.isLoading,
    isFetching: query.isFetching,
    error: query.isError ? query.error?.message || 'Erro ao conectar com o servidor' : '',
    resumoColumns,
    operacoesColumns,
    handleDownloadResumo,
    handleDownloadOperacoes,
    loadData: () => query.refetch(),
    weekOptions,
    selectedSeconda,
    setSelectedSeconda,
    selectedWeekOption,
    isCurrentWeek,
  };
}
